// components/VerdictShareCard.jsx
import React from 'react';
import { getShareUrl } from '../utils/deepLinkConfig';
import { getCanonicalRenderedVerdictAsset, getCanonicalTrialVerdictText } from '../utils/verdictAssets';

const VerdictShareCard = ({ verdictData, user, cardId = 'verdict-share-card' }) => {
  // Handle case where verdictData is undefined or incomplete
  if (!verdictData || !verdictData.trial) {
    console.warn('VerdictShareCard: Missing verdictData or trial');
    return null;
  }

  const trial = verdictData.trial;
  const winner = verdictData.winner;
  const verdictMeta = verdictData?.verdict_data && typeof verdictData.verdict_data === 'object'
    ? verdictData.verdict_data
    : {};
  const canonicalText = getCanonicalTrialVerdictText({
    ...(verdictData || {}),
    trial,
    verdict_data: verdictMeta,
  });
  const overlayLine =
    canonicalText.captionShort ||
    verdictData?.overlay_line ||
    verdictMeta?.overlay_line ||
    verdictData?.verdict_line_short ||
    verdictMeta?.verdict_line_short ||
    null;
  const subverdictLine =
    canonicalText.captionFull ||
    verdictData?.punchline ||
    'The jury has spoken.';
  const shareLine =
    verdictData?.share_line ||
    verdictMeta?.share_line ||
    subverdictLine;
  const imageUrl = getCanonicalRenderedVerdictAsset({
    ...(verdictData || {}),
    trial,
    verdict_data: verdictMeta,
  }).url;
  const winnerPct = verdictData.winnerPct || 50;

  // Determine winner and loser labels
  let winnerLabel, loserLabel;
  if (winner === 'A') {
    winnerLabel = trial.sideA;
    loserLabel = trial.sideB;
  } else if (winner === 'B') {
    winnerLabel = trial.sideB;
    loserLabel = trial.sideA;
  } else {
    // Tie
    winnerLabel = 'TIE';
    loserLabel = `${trial.sideA} vs ${trial.sideB}`;
  }

  return (
    <div
      id={cardId}
      style={{
        position: 'absolute',
        left: '-9999px',
        top: '-9999px',
        width: '1080px',
        height: '1920px',
        background: 'linear-gradient(180deg, #0f172a 0%, #111827 45%, #020617 100%)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        padding: '56px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        gap: '28px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '26px', fontWeight: '800', letterSpacing: '0.24em', textTransform: 'uppercase', color: 'rgba(226,232,240,0.72)' }}>
            GweffIT Verdict
          </div>
          <div style={{ fontSize: '68px', fontWeight: '900', lineHeight: '1.04', marginTop: '14px', maxWidth: '920px' }}>
            {trial.title}
          </div>
        </div>
        <div style={{
          padding: '18px 24px',
          borderRadius: '999px',
          background: 'rgba(15,23,42,0.7)',
          border: '1px solid rgba(148,163,184,0.22)',
          fontSize: '24px',
          fontWeight: '800',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: '#cbd5e1',
        }}>
          {verdictData.verdictType || 'Verdict'}
        </div>
      </div>

      <div style={{
        flex: '1 1 auto',
        borderRadius: '40px',
        overflow: 'hidden',
        background: 'rgba(15,23,42,0.76)',
        border: '1px solid rgba(148,163,184,0.18)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          position: 'relative',
          flex: '0 0 60%',
          background: 'linear-gradient(180deg, rgba(30,41,59,0.65) 0%, rgba(15,23,42,0.92) 100%)',
        }}>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Verdict"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(226,232,240,0.78)',
              fontSize: '48px',
              fontWeight: '800',
            }}>
              Verdict image loading
            </div>
          )}
          <div style={{
            position: 'absolute',
            inset: '0',
            background: 'linear-gradient(180deg, rgba(2,6,23,0.04) 45%, rgba(2,6,23,0.74) 100%)',
          }} />
          <div style={{
            position: 'absolute',
            left: '32px',
            right: '32px',
            bottom: '28px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: '20px',
          }}>
            <div style={{
              maxWidth: '72%',
              fontSize: '20px',
              fontWeight: '800',
              lineHeight: '1.2',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'rgba(248,250,252,0.88)',
            }}>
              {overlayLine || winnerLabel}
            </div>
            <div style={{
              padding: '10px 14px',
              borderRadius: '999px',
              background: 'rgba(15,23,42,0.64)',
              fontSize: '18px',
              fontWeight: '800',
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#e2e8f0',
            }}>
              GweffIT
            </div>
          </div>
        </div>

        <div style={{
          flex: '1 1 auto',
          padding: '36px 36px 34px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '26px',
        }}>
          <div>
            <div style={{
              fontSize: '22px',
              fontWeight: '800',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'rgba(148,163,184,0.86)',
              marginBottom: '18px',
            }}>
              Final Verdict
            </div>
            <div style={{
              fontSize: '68px',
              lineHeight: '1.06',
              fontWeight: '900',
              color: '#f8fafc',
            }}>
              {shareLine}
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'end',
            gap: '24px',
          }}>
            <div>
              <div style={{
                fontSize: '24px',
                fontWeight: '800',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#a78bfa',
                marginBottom: '10px',
              }}>
                {winnerLabel} took it
              </div>
              <div style={{
                fontSize: '30px',
                lineHeight: '1.25',
                color: 'rgba(226,232,240,0.88)',
              }}>
                {winner !== 'Tie' ? `${winnerLabel} over ${loserLabel}` : loserLabel}
              </div>
              <div style={{
                fontSize: '24px',
                color: 'rgba(148,163,184,0.9)',
                marginTop: '12px',
              }}>
                {Math.round(winnerPct)}% of the jury agreed
              </div>
            </div>

            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
                getShareUrl('trial', trial.id) + '?ref=share'
                )}`}
              alt="QR code"
              style={{ width: '220px', height: '220px', borderRadius: '22px', background: 'white', padding: '12px' }}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '42px', fontWeight: '900', color: '#f8fafc' }}>
            GWEFFIT
          </div>
          <div style={{ fontSize: '24px', color: 'rgba(148,163,184,0.92)', marginTop: '6px' }}>
            Settle it. Screenshot it. Send it.
          </div>
        </div>
        <div style={{
          padding: '14px 20px',
          borderRadius: '999px',
          background: 'rgba(167,139,250,0.16)',
          border: '1px solid rgba(167,139,250,0.35)',
          fontSize: '22px',
          fontWeight: '800',
          color: '#e9d5ff',
        }}>
          {trial.totalVotes || 0} votes
        </div>
      </div>
    </div>
  );
};

export default VerdictShareCard;
